// use-user-preference-actions.ts — Current-user suite, preference actions.

'use client';

import { useCallback, useMemo } from 'react';

import {
  CONTENT_RENDER_MODE_RUNTIME_PROBE_FEATURE,
  CONTENT_RENDER_MODE_RUNTIME_PROBE_INSTANCE_ID,
  CONTENT_RENDER_MODE_RUNTIME_PROBE_PREFERENCES_COMPONENT,
  CONTENT_RENDER_MODE_RUNTIME_PROBE_SEED_PREFERENCES_SOURCE,
  CONTENT_RENDER_MODE_RUNTIME_PROBE_SET_PREFERENCES_SOURCE,
  ContentRenderModeRuntimeProbeBranch,
  ContentRenderModeRuntimeProbeEvent,
} from '@/constants/contentRenderModeRuntimeProbe';
import { usePersistentDebugLogger } from '@/hooks/telemetry';
import {
  CHAT_COMPOSER_MINIMUM_ROUNDS,
  SESSION_CONTROL_MINIMUM_ROUNDS,
  type SessionFoldPreferences,
  type SetUserPreferencesCookieValue,
  type UserPreferenceActions,
  type UserPreferenceScalarActions,
  type UserPreferenceSessionFoldActions,
  type UserPreferences,
} from './user-preferences-model';
import {
  mergeSessionFoldPreferences,
  sanitizeUserPreferences,
} from './user-preferences-normalizers';

const SESSION_FOLD_PREFERENCES_COOKIE_ENTRY_LIMIT = 8;

function getBoundedSessionFoldPreferences(input: {
  activeSessionId: string;
  activeSessionPreferences: SessionFoldPreferences;
  previousSessionFoldPreferences: Record<string, SessionFoldPreferences>;
}): Record<string, SessionFoldPreferences> {
  const {
    activeSessionId,
    activeSessionPreferences,
    previousSessionFoldPreferences,
  } = input;
  const recentSessionFoldPreferenceEntries = Object.entries(
    previousSessionFoldPreferences,
  )
    .filter(([sessionId]) => sessionId !== activeSessionId)
    .concat([[activeSessionId, activeSessionPreferences]])
    .slice(-SESSION_FOLD_PREFERENCES_COOKIE_ENTRY_LIMIT);

  return Object.fromEntries(recentSessionFoldPreferenceEntries);
}

function useUserPreferenceScalarActions(
  setCookieValue: SetUserPreferencesCookieValue,
): UserPreferenceScalarActions {
  const setPrefs = useCallback(
    (next: Partial<UserPreferences>) => {
      setCookieValue((previousValue) => ({
        ...sanitizeUserPreferences(previousValue),
        ...next,
      }));
    },
    [setCookieValue],
  );

  const setChatComposerRounds = useCallback(
    (value: number) => {
      if (!Number.isInteger(value) || value < CHAT_COMPOSER_MINIMUM_ROUNDS) {
        return;
      }

      setPrefs({ chatComposerRounds: value });
    },
    [setPrefs],
  );

  const setSessionControlRounds = useCallback(
    (value: number) => {
      if (!Number.isInteger(value) || value < SESSION_CONTROL_MINIMUM_ROUNDS) {
        return;
      }

      setPrefs({ sessionControlRounds: value });
    },
    [setPrefs],
  );

  const setEnterToSend = useCallback(
    (value: boolean) => {
      setPrefs({ enterToSend: value });
    },
    [setPrefs],
  );

  return useMemo(
    () => ({
      setPrefs,
      setChatComposerRounds,
      setSessionControlRounds,
      setEnterToSend,
    }),
    [setChatComposerRounds, setEnterToSend, setPrefs, setSessionControlRounds],
  );
}

function useSetSessionFoldPreferences(
  setCookieValue: SetUserPreferencesCookieValue,
): UserPreferenceSessionFoldActions['setSessionFoldPreferences'] {
  const logger = usePersistentDebugLogger({
    component: CONTENT_RENDER_MODE_RUNTIME_PROBE_PREFERENCES_COMPONENT,
    instanceId: CONTENT_RENDER_MODE_RUNTIME_PROBE_INSTANCE_ID,
    metadata: {
      feature: CONTENT_RENDER_MODE_RUNTIME_PROBE_FEATURE,
      source: CONTENT_RENDER_MODE_RUNTIME_PROBE_SET_PREFERENCES_SOURCE,
    },
  });

  return useCallback(
    (sessionId, next): void => {
      const normalizedSessionId = sessionId.trim();

      if (!normalizedSessionId) {
        logger.logBranchChoice({
          name: ContentRenderModeRuntimeProbeEvent.PreferenceSetRequested,
          selectedBranch: ContentRenderModeRuntimeProbeBranch.EmptySessionId,
          payload: {
            rawSessionId: sessionId,
            nextContentVersionRenderMode: next.contentVersionRenderMode ?? null,
          },
        });
        return;
      }

      logger.logEventFlow({
        name: ContentRenderModeRuntimeProbeEvent.PreferenceSetRequested,
        payload: {
          sessionId: normalizedSessionId,
          next,
          nextContentVersionRenderMode: next.contentVersionRenderMode ?? null,
        },
      });

      setCookieValue((previousValue) => {
        const previousPreferences = sanitizeUserPreferences(previousValue);
        const currentSessionPreferences =
          previousPreferences.sessionFoldPreferences[normalizedSessionId] ??
          previousPreferences.lastSessionLayout;
        const nextSessionPreferences = mergeSessionFoldPreferences({
          current: currentSessionPreferences,
          next,
        });

        logger.logMarker({
          name: ContentRenderModeRuntimeProbeEvent.PreferenceMergeResolved,
          payload: {
            sessionId: normalizedSessionId,
            previousSessionContentVersionRenderMode:
              currentSessionPreferences.contentVersionRenderMode,
            previousLastSessionLayoutContentVersionRenderMode:
              previousPreferences.lastSessionLayout.contentVersionRenderMode,
            nextContentVersionRenderMode: next.contentVersionRenderMode ?? null,
            resolvedContentVersionRenderMode:
              nextSessionPreferences.contentVersionRenderMode,
            resolvedLastSessionLayoutContentVersionRenderMode:
              nextSessionPreferences.contentVersionRenderMode,
          },
        });

        return {
          ...previousPreferences,
          lastSessionLayout: nextSessionPreferences,
          sessionFoldPreferences: getBoundedSessionFoldPreferences({
            activeSessionId: normalizedSessionId,
            activeSessionPreferences: nextSessionPreferences,
            previousSessionFoldPreferences:
              previousPreferences.sessionFoldPreferences,
          }),
        };
      });
    },
    [logger, setCookieValue],
  );
}

function useSeedSessionFoldPreferencesIfAbsent(
  setCookieValue: SetUserPreferencesCookieValue,
): UserPreferenceSessionFoldActions['seedSessionFoldPreferencesIfAbsent'] {
  const logger = usePersistentDebugLogger({
    component: CONTENT_RENDER_MODE_RUNTIME_PROBE_PREFERENCES_COMPONENT,
    instanceId: CONTENT_RENDER_MODE_RUNTIME_PROBE_INSTANCE_ID,
    metadata: {
      feature: CONTENT_RENDER_MODE_RUNTIME_PROBE_FEATURE,
      source: CONTENT_RENDER_MODE_RUNTIME_PROBE_SEED_PREFERENCES_SOURCE,
    },
  });

  return useCallback(
    (sessionId): void => {
      const normalizedSessionId = sessionId.trim();

      if (!normalizedSessionId) {
        logger.logBranchChoice({
          name: ContentRenderModeRuntimeProbeEvent.PreferenceSeedRequested,
          selectedBranch: ContentRenderModeRuntimeProbeBranch.EmptySessionId,
          payload: { rawSessionId: sessionId },
        });
        return;
      }

      logger.logEventFlow({
        name: ContentRenderModeRuntimeProbeEvent.PreferenceSeedRequested,
        payload: { sessionId: normalizedSessionId },
      });

      setCookieValue((previousValue) => {
        const previousPreferences = sanitizeUserPreferences(previousValue);
        if (
          Object.prototype.hasOwnProperty.call(
            previousPreferences.sessionFoldPreferences,
            normalizedSessionId,
          )
        ) {
          const existingSessionPreferences =
            previousPreferences.sessionFoldPreferences[normalizedSessionId];

          logger.logBranchChoice({
            name: ContentRenderModeRuntimeProbeEvent.PreferenceSeedResolved,
            selectedBranch: ContentRenderModeRuntimeProbeBranch.AlreadySeeded,
            payload: {
              sessionId: normalizedSessionId,
              existingContentVersionRenderMode:
                existingSessionPreferences?.contentVersionRenderMode ?? null,
              lastSessionLayoutContentVersionRenderMode:
                previousPreferences.lastSessionLayout.contentVersionRenderMode,
            },
          });

          if (!existingSessionPreferences) {
            return previousPreferences;
          }

          return {
            ...previousPreferences,
            sessionFoldPreferences: getBoundedSessionFoldPreferences({
              activeSessionId: normalizedSessionId,
              activeSessionPreferences: existingSessionPreferences,
              previousSessionFoldPreferences:
                previousPreferences.sessionFoldPreferences,
            }),
          };
        }

        logger.logBranchChoice({
          name: ContentRenderModeRuntimeProbeEvent.PreferenceSeedResolved,
          selectedBranch: ContentRenderModeRuntimeProbeBranch.SeedApplied,
          payload: {
            sessionId: normalizedSessionId,
            seededContentVersionRenderMode:
              previousPreferences.lastSessionLayout.contentVersionRenderMode,
          },
        });

        return {
          ...previousPreferences,
          sessionFoldPreferences: getBoundedSessionFoldPreferences({
            activeSessionId: normalizedSessionId,
            activeSessionPreferences: previousPreferences.lastSessionLayout,
            previousSessionFoldPreferences:
              previousPreferences.sessionFoldPreferences,
          }),
        };
      });
    },
    [logger, setCookieValue],
  );
}

function useUserPreferenceSessionFoldActions(
  setCookieValue: SetUserPreferencesCookieValue,
): UserPreferenceSessionFoldActions {
  const setSessionFoldPreferences =
    useSetSessionFoldPreferences(setCookieValue);
  const seedSessionFoldPreferencesIfAbsent =
    useSeedSessionFoldPreferencesIfAbsent(setCookieValue);

  return useMemo(
    () => ({
      setSessionFoldPreferences,
      seedSessionFoldPreferencesIfAbsent,
    }),
    [seedSessionFoldPreferencesIfAbsent, setSessionFoldPreferences],
  );
}

export function useUserPreferenceActions(
  setCookieValue: SetUserPreferencesCookieValue,
): UserPreferenceActions {
  const scalarActions = useUserPreferenceScalarActions(setCookieValue);
  const sessionFoldActions = useUserPreferenceSessionFoldActions(setCookieValue);

  return useMemo(
    () => ({
      ...scalarActions,
      ...sessionFoldActions,
    }),
    [scalarActions, sessionFoldActions],
  );
}
